import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Loader2, Users, Upload, GraduationCap, FileDown, CalendarRange, CalendarClock, Search, Layers, Printer, MessageSquareText, UserX, ArchiveRestore, Archive, ArrowRightLeft, Pencil, Send, ShieldCheck } from "lucide-react";
import { useWindowed, ShowMoreBar } from "@/components/ShowMore";
import { useDebounced } from "@/lib/useDebounced";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import StudentAttendanceDialog from "@/pages/admin/StudentAttendanceDialog";
import { IconAction } from "@/components/IconAction";
import { viewFromApi } from "@/components/FileViewer";
import { ParentInvitesDialog } from "@/components/ParentInvitesDialog";
import { CarTagsReminder } from "@/components/CarTagsReminder";
import { StudentEditorDialog } from "@/components/StudentEditorDialog";


async function printCarTags(classFilter) {
  try {
    const token = localStorage.getItem("vlog_token");
    const qs = classFilter ? `?class_id=${classFilter}` : "";
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students/car-tags.pdf${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || "No car tags to print");
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "logerd-car-tags.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(classFilter ? "Car tags for this class downloaded" : "Car tags for all students downloaded");
  } catch (e) {
    toast.error(e.message || "Failed");
  }
}

export default function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | student doc
  const [csvBusy, setCsvBusy] = useState(false);
  const [attendanceFor, setAttendanceFor] = useState(null); // student doc | null
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const dq = useDebounced(query.trim(), 300);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);
  const [liveStatus, setLiveStatus] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);
  const [transferTargets, setTransferTargets] = useState({ allowed: false, schools: [] });
  const [transferFor, setTransferFor] = useState(null);
  const [invites, setInvites] = useState(null); // { student_id, student_name, results } | null
  const [transferSel, setTransferSel] = useState({ school: "", grade: "" });
  const [transferBusy, setTransferBusy] = useState(false);

  useEffect(() => {
    api.get("/students/transfer-targets").then(({ data }) => setTransferTargets(data)).catch(() => {});
  }, []);

  const doTransfer = async () => {
    setTransferBusy(true);
    try {
      const { data } = await api.post(`/students/${transferFor.id}/transfer`, {
        to_company_id: transferSel.school, to_class_id: transferSel.grade || "",
      });
      toast.success(`${transferFor.full_name} transferred to ${data.school}`);
      setTransferFor(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer failed");
    } finally {
      setTransferBusy(false);
    }
  };

  const loadArchived = async () => {
    try {
      const { data } = await api.get("/students/archived");
      setArchived(data);
    } catch { /* not fatal */ }
  };

  const withdraw = async (s) => {
    if (!window.confirm(`Deactivate ${s.full_name}? Their record is kept read-only and they're removed from rosters, kiosk and dismissal. You can restore them any time.`)) return;
    try {
      await api.post(`/students/${s.id}/withdraw`);
      toast.success(`${s.full_name} deactivated (read-only)`);
      load();
      if (showArchived) loadArchived();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const restore = async (s) => {
    try {
      await api.post(`/students/${s.id}/restore`);
      toast.success(`${s.full_name} restored`);
      load();
      loadArchived();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };
  const classFilter = searchParams.get("class_id") || "";

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/students/live-status");
        if (alive) setLiveStatus(data);
      } catch { /* not fatal */ }
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const setClassFilter = (v) => setSearchParams(v ? { class_id: v } : {}, { replace: true });

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, c, b] = await Promise.all([api.get("/students", { params: { q: dq || undefined, limit: SERVER_PAGE } }), api.get("/classes"), api.get("/buses")]);
      setStudents(sRes.data); setTotal(totalFrom(sRes));
      setClasses(c.data);
      setBuses(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dq]);

  const classNames = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.period ? `${c.period} · ${c.name}` : c.name])), [classes]);
  const busNumbers = useMemo(() => Object.fromEntries(buses.map((b) => [b.id, b.number])), [buses]);
  const visibleStudents = useMemo(() => {
    return students.filter((s) => {
      if (classFilter && s.class_id !== classFilter && !(s.class_ids || []).includes(classFilter)) return false;
      // text search is done server-side (q param); only class filter applies here
      return true;
    });
  }, [students, query, classFilter]);
  const studentWin = useWindowed(visibleStudents);

  const openNew = () => setEditing("new");

  // Attendance PDF export with date-range picker
  const [pdfFor, setPdfFor] = useState(null); // { id, full_name } | null
  const today = () => new Date().toISOString().slice(0, 10);
  const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); };
  const yearAgo = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10); };
  const [pdfRange, setPdfRange] = useState({ preset: "this_month", from: "", to: "" });
  const [pdfBusy, setPdfBusy] = useState(false);

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "all") return { from: "", to: "" };
    if (preset === "today") return { from: today(), to: today() };
    if (preset === "this_week") {
      const d = new Date(); d.setDate(d.getDate() - d.getDay());
      return { from: d.toISOString().slice(0, 10), to: today() };
    }
    if (preset === "this_month") {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: d.toISOString().slice(0, 10), to: today() };
    }
    if (preset === "last_month") {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
    }
    if (preset === "this_year") {
      const d = new Date(now.getFullYear(), 0, 1);
      return { from: d.toISOString().slice(0, 10), to: today() };
    }
    if (preset === "last_30") return { from: monthAgo(), to: today() };
    if (preset === "last_365") return { from: yearAgo(), to: today() };
    return {};
  };

  const openAttendance = (s) => {
    const r = applyPreset("this_month");
    setPdfRange({ preset: "this_month", from: r.from, to: r.to });
    setPdfFor({ id: s.id, full_name: s.full_name });
  };

  const pickPreset = (preset) => {
    const r = applyPreset(preset);
    setPdfRange({ preset, from: r.from ?? pdfRange.from, to: r.to ?? pdfRange.to });
  };

  const downloadAttendance = async () => {
    if (!pdfFor) return;
    setPdfBusy(true);
    try {
      const params = {};
      if (pdfRange.from) params.from = `${pdfRange.from}T00:00:00Z`;
      if (pdfRange.to) params.to = `${pdfRange.to}T23:59:59Z`;
      const res = await api.get(`/students/${pdfFor.id}/attendance.pdf`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = pdfFor.full_name.replace(/[^A-Za-z0-9_-]+/g, "-").toLowerCase();
      const tag = pdfRange.from && pdfRange.to ? `${pdfRange.from}_to_${pdfRange.to}` : "all-time";
      a.download = `attendance-${safe}-${tag}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${pdfFor.full_name}'s report`);
      setPdfFor(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    } finally {
      setPdfBusy(false);
    }
  };
  const openEdit = (s) => setEditing(s);
  const cancelEdit = () => setEditing(null);
  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam) return;
    api.get(`/students/${editParam}/profile`).then(({ data }) => openEdit(data.student)).catch(() => toast.error("Student not found"));
    setSearchParams((sp) => { sp.delete("edit"); return sp; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam]);

  const inviteParents = async (s) => {
    try {
      const { data } = await api.post(`/students/${s.id}/invite-parents`, { origin_url: window.location.origin });
      setInvites(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send invites");
    }
  };


  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvBusy(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        toast.error("CSV looks empty.");
        return;
      }
      const { data } = await api.post("/students/csv-upload", { rows });
      toast.success(`Imported ${data.created} students.`);
      load();
    } catch (err) {
      toast.error(err.message || "CSV upload failed.");
    } finally {
      setCsvBusy(false);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="students-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">School roster</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Students</h1>
          <p className="text-sm text-slate-600 mt-1">Each student lists the adults (with name + driver's license number) who are authorized to pick them up.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => printCarTags(classFilter)}
            data-testid="print-car-tags-btn"
            title="Printable dashboard tags with each student's car number"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
          >
            <Printer className="h-4 w-4" /> Car tags
          </button>
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 cursor-pointer transition">
            <Upload className="h-4 w-4" /> {csvBusy ? "Uploading…" : "Upload CSV"}
            <input type="file" accept=".csv,text/csv" onChange={onCsv} className="hidden" data-testid="students-csv-input" disabled={csvBusy} />
          </label>
          <button onClick={openNew} data-testid="students-add-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:shadow transition">
            <Plus className="h-4 w-4" /> Add student
          </button>
        </div>
      </header>

      <CarTagsReminder onPrint={() => printCarTags("")} />

      <div className="text-xs text-slate-500 mb-4">
        CSV columns: <code>full_name, student_number, grade, car_number, parent_name, parent_license, parent_relationship, parent_phone, parent_email</code> — car numbers auto-generate when the column is missing
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4 md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="students-search-input"
            placeholder="Search name or car #…"
            className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          />
        </div>
        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          data-testid="students-class-filter"
          className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-primary outline-none"
        >
          <option value="">All grades</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(query || classFilter) && (
          <button
            onClick={() => { setQuery(""); setClassFilter(""); }}
            data-testid="students-clear-filters"
            className="text-xs text-primary hover:underline text-left"
          >
            Clear filters
          </button>
        )}
        <div className="md:ml-auto text-xs text-slate-500" data-testid="students-count">
          Showing <b>{visibleStudents.length}</b> of {students.length}
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="students-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Grade</th>
              <th className="px-5 py-3">Car #</th>
              <th className="px-5 py-3">Right now</th>
              <th className="px-5 py-3">Authorized pickups</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>)}
            {!loading && students.length === 0 && (<tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No students yet. Add one or import a CSV.</td></tr>)}
            {!loading && students.length > 0 && visibleStudents.length === 0 && (<tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No students match your search or filters.</td></tr>)}
            {studentWin.items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`student-row-${s.id}`}>
                <td className="px-5 py-3">
                  <button onClick={() => navigate(`/admin/students/${s.id}`)} data-testid={`student-name-${s.id}`}
                    className="font-medium text-slate-900 flex items-center gap-2 hover:text-primary hover:underline text-left transition">
                    <GraduationCap className="h-4 w-4 text-slate-400" />
                    {s.full_name}
                  </button>
                  {s.student_number && <div className="text-[11px] font-mono text-slate-400" data-testid={`student-number-${s.id}`}>ID {s.student_number}</div>}
                </td>
                <td className="px-5 py-3" data-testid={`student-class-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-1">
                    {s.class_id && classNames[s.class_id] ? (
                      <button onClick={() => setClassFilter(s.class_id)} className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100 transition" title="Homeroom">
                        <Layers className="h-3 w-3" /> {classNames[s.class_id]}
                      </button>
                    ) : s.grade ? <span className="text-slate-700">{s.grade}</span> : (s.class_ids || []).length === 0 ? <span className="text-slate-400">—</span> : null}
                    {(s.class_ids || []).filter((id) => classNames[id] && id !== s.class_id).map((id) => (
                      <button key={id} onClick={() => setClassFilter(id)} data-testid={`student-period-${s.id}-${id}`} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-200 transition" title="Period class">
                        {classNames[id]}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3" data-testid={`student-carnum-${s.id}`}>
                  {s.rider_type === "bus" ? (
                    <span className="inline-flex rounded-md bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-800">
                      BUS {busNumbers[s.bus_id] || "?"}
                    </span>
                  ) : s.car_number ? (
                    <span className="inline-flex rounded-md bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">{s.car_number}</span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-5 py-3" data-testid={`student-livestatus-${s.id}`}>
                  {liveStatus[s.id]?.where === "bus" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                      On bus {liveStatus[s.id].bus_number || ""}
                    </span>
                  ) : liveStatus[s.id]?.where === "class" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      In class
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-5 py-3 text-slate-700 text-xs">
                  {(s.authorized_pickups || []).map((ap, i) => (
                    <div key={i}>{ap.name} {ap.license_number ? `· ${ap.license_number}` : ""}</div>
                  ))}
                  {(!s.authorized_pickups || s.authorized_pickups.length === 0) && <span className="text-slate-400">None</span>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-0.5">
                    <IconAction onClick={() => inviteParents(s)} Icon={Send} label="Invite parents to the app (resend)" testid={`student-invite-${s.id}`} />
                    <IconAction onClick={() => navigate(`/admin/messages?student_id=${s.id}`)} Icon={MessageSquareText} label="Message parents" testid={`student-message-${s.id}`} />
                    <IconAction onClick={() => setAttendanceFor(s)} Icon={CalendarClock} label="Attendance & excuses" testid={`student-attendance-${s.id}`} />
                    <IconAction onClick={() => openAttendance(s)} Icon={FileDown} label="Attendance PDF" testid={`student-attendance-pdf-${s.id}`} />
                    <IconAction onClick={() => viewFromApi(api, `/students/${s.id}/record.pdf`, { title: `${s.full_name} — full record`, filename: `record-${s.id}.pdf`, type: "application/pdf" })} Icon={ShieldCheck} label="Download full record (HIPAA right of access)" testid={`student-record-${s.id}`} />
                    <IconAction onClick={() => openEdit(s)} Icon={Pencil} label="Edit" testid={`student-edit-${s.id}`} tone="primary" />
                    {transferTargets.allowed && (
                      <IconAction onClick={() => { setTransferFor(s); setTransferSel({ school: "", grade: "" }); }} Icon={ArrowRightLeft} label="Transfer to another school" testid={`student-transferbtn-${s.id}`} />
                    )}
                    <IconAction onClick={() => withdraw(s)} Icon={UserX} label="Deactivate (archive, read-only)" testid={`student-withdraw-${s.id}`} tone="danger" />
                  </div>
                </td>
              </tr>
            ))}
            <ShowMoreBar win={studentWin} colSpan={6} testid="students-show-more" />
            {!studentWin.hasMore && total > students.length && (
              <tr><td colSpan={6}><LoadMoreBar shown={students.length} total={total} loading={moreLoading} testid="students-load-more" onMore={async () => {
                setMoreLoading(true);
                try { const res = await api.get("/students", { params: { q: dq || undefined, limit: SERVER_PAGE, offset: students.length } }); setStudents((p) => [...p, ...res.data]); setTotal(totalFrom(res)); }
                finally { setMoreLoading(false); }
              }} /></td></tr>
            )}
          </tbody>
        </table>
      </section>

      <div className="mt-4">
        <button
          onClick={() => { const v = !showArchived; setShowArchived(v); if (v) loadArchived(); }}
          data-testid="toggle-archived-btn"
          className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-900 transition"
        >
          <Archive className="h-3.5 w-3.5" /> {showArchived ? "Hide archived students" : "Show archived students"}
        </button>
        {showArchived && (
          <section className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden" data-testid="archived-students-section">
            <div className="px-5 py-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500">
              Archived students ({archived.length})
            </div>
            {archived.length === 0 ? (
              <div className="px-5 py-6 text-sm text-slate-400">No archived students.</div>
            ) : archived.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 last:border-0" data-testid={`archived-row-${s.id}`}>
                <GraduationCap className="h-4 w-4 text-slate-300" />
                <span className="flex-1 text-sm text-slate-700">{s.full_name}</span>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  s.external_id === "graduated" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"
                }`}>{s.external_id || "archived"}</span>
                <button onClick={() => restore(s)} data-testid={`student-restore-${s.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition">
                  <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                </button>
              </div>
            ))}
          </section>
        )}
      </div>

      {/* Attendance & excuses dialog */}
      <Dialog open={!!transferFor} onOpenChange={(o) => !o && setTransferFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="student-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-primary" /> Transfer {transferFor?.full_name}
            </DialogTitle>
            <DialogDescription>
              Moves the student to another school in your district. Car number is re-assigned there; parents and pickup contacts stay linked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Destination school</span>
              <select value={transferSel.school} onChange={(e) => setTransferSel({ school: e.target.value, grade: "" })}
                data-testid="student-transfer-school" className={inputCls}>
                <option value="">Pick a school…</option>
                {transferTargets.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            {transferSel.school && (
              <label className="block">
                <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Grade</span>
                <select value={transferSel.grade} onChange={(e) => setTransferSel({ ...transferSel, grade: e.target.value })}
                  data-testid="student-transfer-grade" className={inputCls}>
                  <option value="">No grade yet (assign later)</option>
                  {(transferTargets.schools.find((s) => s.id === transferSel.school)?.grades || []).map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <DialogFooter>
            <button onClick={doTransfer} disabled={transferBusy || !transferSel.school} data-testid="student-transfer-submit"
              className="h-10 rounded-lg bg-primary px-6 text-sm font-medium text-white inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-50 transition">
              {transferBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />} Transfer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {attendanceFor && (
        <StudentAttendanceDialog student={attendanceFor} onClose={() => setAttendanceFor(null)} />
      )}
      <ParentInvitesDialog data={invites} onClose={() => setInvites(null)} />

      {/* Attendance PDF date-range picker */}
      <Dialog open={!!pdfFor} onOpenChange={(o) => !o && setPdfFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="attendance-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-primary" /> Attendance report
            </DialogTitle>
            <DialogDescription>
              Pick a date range for {pdfFor?.full_name}'s PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "this_month", label: "This month" },
                { v: "last_month", label: "Last month" },
                { v: "this_year", label: "This year" },
                { v: "last_30", label: "Last 30 days" },
                { v: "last_365", label: "Last 12 months" },
                { v: "this_week", label: "This week" },
                { v: "today", label: "Today" },
                { v: "all", label: "All time" },
                { v: "custom", label: "Custom range" },
              ].map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => pickPreset(p.v)}
                  data-testid={`pdf-preset-${p.v}`}
                  className={`h-9 rounded-lg border text-xs font-medium transition ${
                    pdfRange.preset === p.v
                      ? "bg-slate-900 text-white border-slate-900"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">From</span>
                <input
                  type="date"
                  value={pdfRange.from}
                  onChange={(e) => setPdfRange({ ...pdfRange, from: e.target.value, preset: "custom" })}
                  data-testid="pdf-from"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                />
              </label>
              <label className="block">
                <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">To</span>
                <input
                  type="date"
                  value={pdfRange.to}
                  onChange={(e) => setPdfRange({ ...pdfRange, to: e.target.value, preset: "custom" })}
                  data-testid="pdf-to"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                />
              </label>
            </div>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setPdfFor(null)} data-testid="pdf-cancel" className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="button" onClick={downloadAttendance} disabled={pdfBusy} data-testid="pdf-download" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF
              </button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {editing && (
        <StudentEditorDialog student={editing} classes={classes} buses={buses} onClose={cancelEdit}
          onSaved={(created, form) => { if (created && (form.authorized_pickups || []).some((p) => (p.phone || "").trim())) inviteParents(created); load(); }} />
      )}
    </div>
  );
}

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

// Tiny CSV parser (handles commas and quoted fields with embedded commas)
function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] || ""; });
    return row;
  });
}
function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === "," && !inQ) {
      out.push(cur);
      cur = "";
    } else { cur += c; }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
